import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { buildWorld, updateClockHands, type WorldBuildResult } from './RoomFactory';
import { LightingRig } from './Lighting';

export class World {
  readonly scene = new THREE.Scene();
  readonly build: WorldBuildResult;
  readonly lighting = new LightingRig();
  private composer: EffectComposer | null = null;
  private bloom: UnrealBloomPass | null = null;
  private renderer: THREE.WebGLRenderer;

  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer;
    this.scene.background = new THREE.Color(0x0c1018);
    this.scene.fog = new THREE.FogExp2(0x121820, 0.018);

    this.build = buildWorld();
    this.scene.add(this.build.group);
    this.scene.add(this.lighting.group);

    updateClockHands(
      this.build.refs.clockHands.hour,
      this.build.refs.clockHands.minute,
      12,
      0,
    );
  }

  setupPostprocessing(camera: THREE.Camera): void {
    const size = new THREE.Vector2();
    this.renderer.getSize(size);
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, camera));
    this.bloom = new UnrealBloomPass(size, 0.28, 0.5, 0.82);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());
  }

  resize(w: number, h: number): void {
    this.composer?.setSize(w, h);
    this.bloom?.resolution.set(w, h);
  }

  render(camera: THREE.Camera): void {
    if (this.composer) this.composer.render();
    else this.renderer.render(this.scene, camera);
  }

  setClock(hour: number, minute: number): void {
    updateClockHands(
      this.build.refs.clockHands.hour,
      this.build.refs.clockHands.minute,
      hour,
      minute,
    );
  }

  openDrawer(which: 'living' | 'desk'): void {
    const d = which === 'living' ? this.build.refs.drawer : this.build.refs.deskDrawer;
    d.position.z += which === 'living' ? 0.35 : 0.4;
  }

  openSafe(): void {
    this.build.refs.safeDoor.rotation.y = -1.4;
    this.build.refs.safeDoor.position.x -= 0.15;
  }

  openBasementDoor(): void {
    const d = this.build.refs.basementDoor;
    d.rotation.y = Math.PI / 2;
    d.position.z += 0.8;
    // コライダ無効化は Game 側でフィルタ
  }

  openHiddenWall(): void {
    const w = this.build.refs.hiddenWall;
    w.position.x -= 2.5;
    w.visible = false;
  }

  openExitDoor(): void {
    const d = this.build.refs.exitDoor;
    d.rotation.y = -Math.PI / 2;
    d.position.x -= 0.9;
  }

  setEmblemLit(on: boolean): void {
    const m = this.build.refs.emblem.material as THREE.MeshStandardMaterial;
    if (on) {
      m.emissive = new THREE.Color(0xffdd88);
      m.emissiveIntensity = 1.5;
    } else {
      m.emissive = new THREE.Color(0x000000);
      m.emissiveIntensity = 0;
    }
  }

  setFuseInstalled(on: boolean): void {
    const m = this.build.refs.fuseSlot.material as THREE.MeshStandardMaterial;
    m.color.set(on ? 0x44aa66 : 0x333333);
    m.emissive = new THREE.Color(on ? 0x226633 : 0x000000);
    m.emissiveIntensity = on ? 0.5 : 0;
  }

  openCabinet(): void {
    this.build.refs.cabinetDoor.rotation.y = 1.2;
    this.build.refs.cabinetDoor.position.x -= 0.2;
  }

  setBrazierLit(on: boolean): void {
    this.build.refs.brazierFlame.visible = on;
  }
}
